/**
 * Self-healing guard for wouter's history monkey-patch.
 *
 * wouter v3 wraps `history.pushState` / `history.replaceState` ONCE at module
 * load so each call dispatches a same-named `Event` that its
 * `useSyncExternalStore` subscription re-renders on. The wrap is guarded by a
 * `Symbol.for("wouter_v3")` marker on `window`, so it is never re-applied.
 *
 * Third-party scripts loaded on the page (chat widgets, analytics, maps) can
 * snapshot `history.pushState` and later restore the NATIVE function,
 * silently stripping wouter's wrapper. From that moment every in-app
 * navigation updates the URL but dispatches no event: the router never
 * re-renders, the old page stays visible, and only a hard refresh (or the
 * Back button, via native `popstate`) recovers. This exact failure was
 * reproduced in production-like sessions: `history.pushState.toString()`
 * returned `[native code]` while wouter's listeners were still subscribed.
 *
 * This module re-installs an equivalent event-dispatching wrapper whenever it
 * detects the patch has been stripped. Our wrappers carry a marker property.
 * A replacement may be native OR a third-party wrapper; observe whether the
 * original emits its event and supply it only when absent. This preserves
 * wouter's intact patch without double-dispatching.
 *
 * Repair triggers:
 *  - capture-phase mouse, keyboard and click listeners (Radix tabs navigate on
 *    mousedown/keydown, before a click listener can repair the method),
 *  - a low-frequency interval as a safety net.
 */

// Delegating wrappers (such as a mounted form's leave guard) retain this
// marker so repair triggers do not wrap them again and strand them on cleanup.
export const HISTORY_EVENTS_GUARD_MARKER = "__padpulse_history_guard__";

type HistoryMethod = "pushState" | "replaceState";

function isOurs(fn: unknown): boolean {
  return (
    typeof fn === "function" &&
    (fn as unknown as Record<string, unknown>)[HISTORY_EVENTS_GUARD_MARKER] === true
  );
}

function wrap(type: HistoryMethod): void {
  const original = history[type];
  const wrapper = function (this: History) {
    // eslint-disable-next-line prefer-rest-params
    const args = arguments;
    let emitted = false;
    const observe = () => { emitted = true; };
    addEventListener(type, observe);
    let result: unknown;
    try {
      result = (original as (...a: unknown[]) => unknown).apply(this, args as unknown as unknown[]);
    } finally {
      removeEventListener(type, observe);
    }
    if (!emitted) {
      const event = new Event(type);
      // wouter attaches the call's arguments object; mirror that exactly.
      (event as unknown as { arguments: unknown }).arguments = args;
      dispatchEvent(event);
    }
    return result;
  };
  (wrapper as unknown as Record<string, unknown>)[HISTORY_EVENTS_GUARD_MARKER] = true;
  history[type] = wrapper as unknown as History["pushState"];
}

/** Re-wrap replaced methods, retaining any events emitted by their original. */
export function ensureHistoryEventsPatched(): boolean {
  if (typeof history === "undefined" || typeof window === "undefined") {
    return false;
  }
  let repaired = false;
  for (const type of ["pushState", "replaceState"] as const) {
    const fn = history[type];
    if (!isOurs(fn)) {
      wrap(type);
      repaired = true;
    }
  }
  if (repaired) {
    // If the URL was already changed through the un-patched method, the
    // router is stale right now — nudge it to re-read the location.
    dispatchEvent(new Event("pushState"));
    console.warn(
      "[history-guard] history.pushState/replaceState had been restored to " +
        "replaced by a third-party script; re-installed the router event patch.",
    );
  }
  return repaired;
}

let installed = false;

/** Install repair triggers. Idempotent; call once at app boot. */
export function installHistoryEventsGuard(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  // Repair immediately in case a third-party script already stripped the
  // patch before this module ran (and before any click/interval fires).
  ensureHistoryEventsPatched();
  // Repair just-in-time: capture phase runs before link click handlers
  // (wouter's <Link> and navigate() calls) process the navigation.
  for (const event of ["mousedown", "keydown", "click"]) {
    document.addEventListener(event, () => ensureHistoryEventsPatched(), true);
  }
  // Safety net for programmatic navigations not preceded by a click.
  window.setInterval(ensureHistoryEventsPatched, 3000);
}
